import { type ReactNode } from 'react';
import {
  Button,
  Tag,
  Toggletip,
  ToggletipActions,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import type {
  NodeKind,
  NodeSource,
  NodeUnit,
  SlotInputNode,
  TearsheetDate,
} from 'types/packages-api/slot-valuation-inputs';
import { TearsheetLink } from './tearsheet-link';
import { type EditControl } from './editability';
import { REPORTED_LOCK_MESSAGE } from '../schedule-dialog/schedule-date-cell';
import {
  derivationKindLabel,
  EMPTY,
  emptyValueNote,
  emptyValueTitle,
  formatNodeDisplay,
  formatNodeValue,
  isUnitUnestablished,
  missingRecordNote,
  overrideOperationKey,
  seriesPreviewCaption,
  siblingFieldList,
  valueSourceState,
  type OperationField,
  type SeriesExtent,
} from './utils';
import {
  overrideBadge,
  overrideBadgeText,
  PENDING_VALUE_MAX,
} from './override-badge';
import styles from './slot-tearsheet.module.scss';

/**
 * One resolved node's value cell. A scalar node shows its single value; a monthly
 * node (has `seriesKey`) shows a `monthly · N` tag plus the ≤4-point preview
 * (contract § SlotInputNode). An unresolved node (`applied: false` / empty
 * `values`) renders faded — a real "no such value", not an error.
 *
 * @param {object} props - The resolved slot-input node.
 * @return {JSX.Element} The value cell content.
 */
export function NodeValue({
  node,
  unit,
  source,
  extent,
}: {
  node: SlotInputNode;
  unit?: NodeUnit;
  /** The node's served `source`. Undefined until the model map lands. */
  source?: NodeSource;
  extent?: SeriesExtent;
}) {
  if (!node.applied || node.values.length === 0) {
    // ── A FUNCTION'S FORMULA *IS* ITS VALUE, SO IT GOES IN THE VALUE CELL ─────────────
    //
    // The ask: "is there any way we could make the formulas the values for the functions so it's
    // not so empty?" Yes, and it is the more honest rendering:
    // `capex(month) = afe_cost allocated to spud_date and first_production_date rows` says
    // strictly more than "Not stored", which is true and tells a reader nothing.
    //
    // A MOVE, NOT AN ADDITION. This formula already rendered, as a caption under the label
    // in the name cell; it is relocated here rather than printed twice, and a test asserts
    // it appears exactly once per row.
    //
    // NOTHING WAS DISPLACED WHEN THIS WAS WRITTEN. Measured on EC 61's duc slot: of the 134
    // scalar zone-table rows, 27 carried formula text and ZERO of those carried a value — so
    // no number competed with a formula for this cell. If that ever stopped being true the
    // value would win, because this branch is only reached when there is no value.
    //
    // ⚠️ IT HAS SINCE STOPPED BEING TRUE, AND THE VALUE DOES WIN. Re-measured 25 Sep 2026:
    // `volume_adjustment_ratio` is bound to the cashflows parquet AND carries the formula
    // it was later given, so its formula renders NOWHERE on this page — the row shows the
    // ratio. It is the only such node today and tearsheet-table-view.test.tsx pins that set
    // by name, so a second one fails an example rather than quietly losing its formula.
    //
    // The behaviour is left as it is on purpose: a reader who opens a row bound to a real
    // column wants the number this run produced, and the derivation is still on the model
    // map. Changing it would mean deciding where a formula goes when a value is also there,
    // which is a design question and not this ticket's.
    if (node.formula) {
      return <span className={styles.formulaValue}>{node.formula}</span>;
    }
    // ── TWO REASONS A CELL IS EMPTY, AND THEY ARE NOT THE SAME FACT ────────────────────
    //
    // A `declared_only` node has no source at all — it is a step in the model, and the dash
    // is the absence of something that was never going to be there. A node with a real
    // source and no value is this evaluation not having recorded one. Drawing both as a bare
    // faded dash is most of why a correct page reads as broken.
    //
    // NEITHER IS HIDDEN. Both rows stay, and each says which it is; hiding them would leave
    // a reader unable to tell an unsourceable node from a dropped value, which is the whole
    // job of the page. While the source is unknown (the map has not landed, or the shape is
    // one this build does not parse) it renders exactly as it did before — a bare dash,
    // claiming nothing.
    const state = valueSourceState(source);
    // A unit the backend could not convert is the row's own note ("Unit not recognised"), not
    // "Not recorded": the value was recorded, in a unit the row can't show.
    const note = node.unitNotRecognised ? null : emptyValueNote(state);
    return (
      <span
        className={styles.nodeValueFaded}
        title={node.unitNotRecognised ? undefined : emptyValueTitle(state)}
      >
        {EMPTY}
        {note && <span className={styles.emptyReason}>{note}</span>}
      </span>
    );
  }
  if (node.seriesKey) {
    return (
      <span className={styles.nodeValueMonthly}>
        <Tag size="sm" type="outline">
          monthly{node.seriesLength ? ` · ${node.seriesLength}` : ''}
        </Tag>
        {/* THE PREVIEW IS UNCONDITIONAL AGAIN, AND THAT IS NOT A REVERSAL.
            968cef311f suppressed it where a disclosure column replaced it; a disclosed node
            now has no ROW at all (parameterRowNodes), so every monthly node that reaches
            this branch is one with NO column anywhere on the page — and for that node these
            four values are the only values a reader gets. The `disclosed` prop is gone
            because it could only ever be false here, and a parameter that cannot vary is a
            branch that cannot be tested. */}
        <span className={styles.nodeSeriesPreview}>
          {node.values
            .map(
              (entry) => `${entry.label}: ${formatNodeValue(entry.value, unit)}`
            )
            .join(', ')}
        </span>
        {/* Says WHICH four these are. Four true numbers with no frame read as the
            whole series; they are the earliest four (node_binder.rb:104). */}
        {seriesPreviewCaption(
          node.values.length,
          node.seriesLength,
          extent
        ) && (
          <span className={styles.nodeSeriesCaption}>
            {seriesPreviewCaption(
              node.values.length,
              node.seriesLength,
              extent
            )}
          </span>
        )}
      </span>
    );
  }
  // `unknown` and `dimensionless` both render bare, so the unestablished one is titled —
  // they are different facts and must not read identically. See isUnitUnestablished.
  // formatNodeDisplay, not formatNodeValue: a few rows round on the page only (the
  // formation subsea elevation to 2 dp), while the export keeps full precision.
  // A DCA forecast shows its name, served beside its id (valueLabel), as plain text.
  const formatted =
    node.valueLabel ?? formatNodeDisplay(node.key, node.values[0].value, unit);
  // ── A VALUE THAT IS ALSO AN ADDRESS ────────────────────────────────────────────────────
  //
  // Only where the SERVER resolved one. `valueHref` comes from a row lookup on the value
  // (node_binder.rb VALUE_HREF_RESOLVERS) and is null wherever that row does not exist — a
  // pdp slot's volume source, for instance, which names no type curve. Never derived here
  // from the value's text, which is how a dead link ships.
  // ...and where it ran and found NOTHING, the row says so. Two identical-looking ids with
  // one clickable and one not reads as a dead link; this is what stops it. See
  // missingRecordNote for why the register is flat.
  const missing = missingRecordNote(node.valueRecordMissing);
  return (
    <span
      title={isUnitUnestablished(unit) ? 'Unit not established' : undefined}
    >
      {node.valueHref ? (
        <TearsheetLink href={node.valueHref}>{formatted}</TearsheetLink>
      ) : (
        formatted
      )}
      {missing && <span className={styles.missingRecord}>{missing}</span>}
    </span>
  );
}

/**
 * The one-word marker saying a row is a DERIVATION rather than a value.
 *
 * FROM THE REGISTRY'S OWN `kind`, on the payload's first response — no model-map wait, and
 * no second notion of "is this a formula row". Quiet, and PRINTED beside the label rather
 * than hovered, because the label is what a reader compares when two rows look alike.
 *
 * @param {object} props - The node's declared kind.
 * @return {(JSX.Element|null)} The marker, or null for a row that carries a value.
 */
export function KindMarker({ kind }: { kind: NodeKind }) {
  const label = derivationKindLabel(kind);
  if (!label) {
    return null;
  }
  return <span className={styles.kindMarker}>{label}</span>;
}

/**
 * A value whose UNIT has not arrived yet.
 *
 * ── AN UNFORMATTED NUMBER IS NOT AN ACCEPTABLE INTERMEDIATE STATE ON THIS PAGE ───────
 *
 * The model map is a second request, so for a window on first paint `meta[key]?.unit` is
 * undefined. `formatOutputValue` then falls through to its no-unit default, and a real
 * `irr` of 0.1852 renders "0" — the exact defect a4fcb229bb fixed, reappearing inside a
 * loading window and WORSE than the original in one respect: no test that waits for the
 * page to settle can catch it.
 *
 * A page whose purpose is explaining where a number came from cannot print a number whose
 * scale it has not established. So it prints nothing numeric until it can. It does NOT
 * guess the unit from the magnitude — that is the trap the whole units effort exists to
 * close — and it does NOT render 0.
 *
 * NARROW, DELIBERATELY. Only a NUMBER with no declared unit is pending: a string routes
 * through the date formatter regardless of unit, and once the map HAS resolved an unknown
 * key will never gain a unit, so it falls back to a best-effort render rather than hanging
 * on "pending" forever.
 *
 * @return {JSX.Element} The pending indicator.
 */
export function PendingValue() {
  return (
    <span
      className={styles.valuePending}
      title="Waiting for the unit definition"
    >
      …
    </span>
  );
}

/**
 * ── THE ROW-LEVEL AFFORDANCE ─────────────────────────────────────────────────────────────
 *
 * The block marker says this section contains something editable; this says WHICH row, and
 * is the control that opens the edit. The rows that can be overridden must carry
 * a visible indication, so both scales exist and both read the same `editableKeys` set.
 *
 * ── LOCKED IS RENDERED, NOT HIDDEN ───────────────────────────────────────────────────────
 *
 * A reported milestone is overridable as a NODE and locked on THIS slot. Dropping the
 * control silently would read as "this row is not editable", which is a different and wrong
 * statement — the field is editable, this well simply reports it. So a locked row shows the
 * lock and explains it, reusing the schedule dialog's Toggletip and ITS copy verbatim
 * (REPORTED_LOCK_MESSAGE, schedule-date-cell.tsx:28). No new lock wording exists here.
 *
 * @param {object} props - The node, whether it is editable/locked, and the open handler.
 * @return {(JSX.Element|null)} The control, the lock, or nothing.
 */
export function OverrideMarker({
  node,
  unit,
  onRerun,
  operations,
  onRevert,
  merged,
  meta,
  rowLabel,
}: {
  node: SlotInputNode;
  unit?: NodeUnit;
  /**
   * This ROW's name, for the actions' accessible names. The node's label everywhere except
   * the three scheduled dates, where the node is named for its source and the row for its
   * milestone — the same distinction {@link EditAffordance} draws with `announced`.
   */
  rowLabel?: string;
  /** The route out of the pending state. Omitted by a caller with no run control to route to. */
  onRerun?: () => void;
  /** Removes this field's override. Omitted by a caller
   *  with no override log, in which case no Revert is offered. */
  onRevert?: (node: SlotInputNode) => void;
  /** This slot's overridden fields grouped by the operation that set them.
   *  Omitted by a caller that has not built it; the operation line is then not shown. */
  operations?: Map<string, OperationField[]>;
  /** The MERGED date this row renders, on the Milestones table only.
   *
   *  THE BADGE MUST DESCRIBE ITS OWN ROW. A milestone cell shows the date merger's answer,
   *  not the registry node's: `modeledCompletionDate` reports `source: 'override'` while
   *  `completion_date` still reads the scheduler's untouched blob. Binding the badge to the
   *  node left one row stating two different facts — the value said the override was in use
   *  and the badge said it was pending. Absent on the node-bound rows, where the cell IS the
   *  node value and the node's own state is the right one. */
  merged?: TearsheetDate;
  /** The node's served definition, for the gate above — only `overrideReachesRow` is read.
   *  Absent while the model map is loading, which reads as "can carry": the same direction
   *  the rest of this file takes when the definition has not arrived. */
  meta?: {
    overrideReachesRow?: boolean;
  };
}) {
  const badge = overrideBadge(node, unit, { merged, meta });
  if (!badge) {
    return null;
  }
  const { entry, applied, hasDefault, previous, explanation, pendingValue } =
    badge;
  // This row's name for the actions below; the node's label when the caller has no
  // row-specific one (every zone row but the three scheduled dates).
  const announcedRow = rowLabel ?? node.label;
  // ── WHICH OPERATION SET THIS VALUE ────────────────────────────────────────────────────
  //
  // ABSENT SAYS NOTHING. `overriddenBy` is missing on a payload cached from before the
  // resolver reported it, and defaulting that to "set individually" would be a claim about an
  // edit nobody recorded. The badge still renders; only this line is withheld.
  //
  // WHAT THE TWO KINDS MAY BE CALLED. `bulk` records that the entry came from the grid's bulk
  // write path and `individual` from a single-row one. It is NOT a claim about how many slots
  // the edit reached — a bulk entry carrying one slot id is ordinary (EC 65's entries [0] and
  // [1] are exactly that) — so neither string mentions scope.
  //
  // THE SIBLING CLAUSE IS THE PART A READER CAN CHECK. "also set Permit Date" is verifiable
  // against the rows in front of them, and it is the honest substitute for naming an
  // operation the log gives no name to.
  const winner = entry.overriddenBy;
  const siblings = winner
    ? siblingFieldList(operations?.get(overrideOperationKey(winner)), node.key)
    : '';
  const operationNote = !winner
    ? null
    : winner.kind === 'bulk'
      ? siblings
        ? `Set by a bulk edit, which also set ${siblings} on this slot.`
        : 'Set by a bulk edit.'
      : siblings
        ? `Set by an edit to this slot, which also set ${siblings}.`
        : 'Set by an edit to this slot.';
  const fullExplanation = operationNote
    ? `${explanation} ${operationNote}`
    : explanation;
  const pendingIsLong =
    pendingValue !== null && pendingValue.length > PENDING_VALUE_MAX;
  const pendingShown = pendingIsLong
    ? `${pendingValue.slice(0, PENDING_VALUE_MAX - 1)}…`
    : pendingValue;
  const badgeText = overrideBadgeText(applied, pendingShown);

  return (
    <Toggletip align="top">
      {/* The accessible name STARTS WITH THE VISIBLE TEXT (WCAG 2.5.3, label in name), so a
          voice user can say what they see, and names the row, because the label replaces
          the button's content and "Overridden" alone does not say which row it is. */}
      <ToggletipButton
        label={`${announcedRow}: ${badgeText}. ${fullExplanation}`}
      >
        <span
          className={
            applied ? styles.overrideMarker : styles.overridePendingMarker
          }
        >
          {badgeText}
        </span>
      </ToggletipButton>
      <ToggletipContent>
        {explanation}
        {/* ONLY WHEN THE BADGE HAD TO CUT IT. A Volume Source name runs past what a row can
            carry, so the badge truncates and the untruncated value goes here — the one place
            already open. Not shown when the badge fits, which would just repeat it. */}
        {pendingIsLong && <p>Pending value: {pendingValue}</p>}
        {/* EXTENDED, NOT A THIRD BADGE. This is a third fact about the SAME override, and the
            row already carries a badge announcing the state; a second chip beside it would
            treble the furniture on every overridden row to say something a reader only asks
            once. It sits in the toggletip the badge already opens. */}
        {operationNote && <p>{operationNote}</p>}
        {/* ── A ROUTE OUT OF THE PENDING STATE ────────────────────────────────────────
            Offered ONLY while the override has not applied — that is the one state where
            "calculate again" is the user's next step; on an applied override it would be
            an invitation to re-run for no reason.

            A ROUTE, NOT A RUN. This does not POST anything. It closes the tearsheet and
            leaves the user at Eval Settings, where the existing Calculate button and its
            existing create-new-eval checkbox are — so the choice between updating this
            evaluation in place and forking a new one stays exactly where it already lives,
            made once, with the copy that already explains it. A second trigger here would
            have to pick that branch on the user's behalf, and the default branch is the
            in-place one, which orphans the previous run's blobs.

            The sentence below is NEW COPY, and it is deliberately about what the CLICK does
            rather than about what a run does — this affordance cannot promise the latter. */}
        {/* ── REVERT ─────────────────────────────────────────────────────────────────
            Appends an UNSET entry, which clears the field at its position in the log. It is
            deliberately not "write the default back as a new override": that leaves the row
            marked overridden forever, which is the state Revert exists to leave.

            WHAT MOVES, AND WHEN. The badge clears on the next read for every field. The
            VALUE moves immediately only where the node reads the slot row; on the nine
            blob-sourced fields the number does not change until the evaluation is next
            calculated — the same rule that governs setting an override, so the sentence
            below is the one already used for the pending state.

            The copy names what it will restore ONLY when the payload actually carries it —
            `overriddenDefault` is absent for a field whose underlying value was overwritten
            in the blob, and inventing one there would be the fabrication this whole
            distinction exists to prevent. */}
        {onRevert && (
          <p>
            {hasDefault
              ? `Removes this override. The value returns to ${previous}.`
              : 'Removes this override. The value it returns to is not recorded in this evaluation.'}
          </p>
        )}
        {!applied && onRerun && (
          <p>
            Closes this tearsheet and returns you to Eval Settings, where
            Calculate is.
          </p>
        )}
        {/* ── THE ACTIONS BELONG IN CARBON'S OWN ACTIONS ROW ──────────────────────────
            Both buttons used to sit INSIDE their sentence's `<p>`. A Carbon Button is an
            inline-flex box with `min-height: 2rem`; sharing a line box with a bare text node
            at `vertical-align: baseline` put the sentence on a different baseline from the
            button and inflated the line — the "letters are not aligned" report.
            `ToggletipActions` is the component for this: `display: flex; align-items:
            center; justify-content: space-between`, so the buttons get their own row and the
            prose is left alone. */}
        {(onRevert || (!applied && onRerun)) && (
          <ToggletipActions>
            {/* ── ⚠️ ROW-SPECIFIC ACCESSIBLE NAMES ────────────────────────────────────
                Both buttons announced only their visible text, so a screen reader moving
                down the table heard "Revert, Revert, Revert" with nothing to say WHICH
                override each one removes. `EditAffordance` already solves this for the
                pencil (`Edit ${announced}`); these are the other two controls in the same
                column and get the same treatment from the same label. */}
            {onRevert && (
              <Button
                kind="ghost"
                size="sm"
                onClick={() => onRevert(node)}
                aria-label={`Revert ${announcedRow} override`}
              >
                Revert
              </Button>
            )}
            {!applied && onRerun && (
              <Button
                kind="ghost"
                size="sm"
                onClick={onRerun}
                aria-label={`Go to Calculate to apply ${announcedRow}`}
              >
                Go to Calculate
              </Button>
            )}
          </ToggletipActions>
        )}
      </ToggletipContent>
    </Toggletip>
  );
}

/**
 * ── ⚠️ "not used", IN THE COLUMN WHERE THE EYE ALREADY IS ────────────────────────────────
 *
 * A note where values are not in use was asked for. One exists as a block caption, and
 * it was scanned past twice: small grey text above forty rows, beside a blue Editable badge
 * that says roughly the opposite. `locked` and `Not recorded` work because they sit in the
 * row, in the right-hand column. This is the same shape in the same place, for the same
 * reason.
 *
 * ⚠️ TWO WORDS, AND THE SENTENCE IS IN THE TOGGLETIP. A sentence in this column does not fit
 * and is not read — the lock marker made that decision already and this follows it rather
 * than re-litigating it.
 *
 * ⚠️ IT DOES NOT REPLACE THE EDIT BUTTON. A row can be both unused by this run and editable
 * for the next one — the five AFE cost DATES are exactly that — so the marker renders beside
 * the affordance rather than in place of it. `locked` cannot collide with it: measured, the
 * lock applies only to the four schedule milestones and none of them is in the not-used set.
 *
 * ── A ROW NOTE, IN THE SAME COLUMN (INS-13307) ───────────────────────────────────────────
 *
 * The same shape carries a softer fact: the value was used, under a condition the row alone
 * does not say (a split well's mixed options). One word in the column, the sentence in the
 * toggletip.
 *
 * ⚠️ THE ACCESSIBLE NAME CARRIES THE ROW AND THE VISIBLE WORD (WCAG 2.5.3, label in name), so
 * a voice-control user can say what they see. In a column of these, "not used" alone is eleven
 * identical announcements, so the row leads, as on the lock and the Edit button. The not-used
 * sentences already start with "Not used:", so the word is added only when the text does not
 * already begin with it: `<row>: Not used: …`, `<row>, note: …`.
 *
 * @param {object} props - The row's announced name, the sentence, the visible word and its
 *   class.
 * @return {JSX.Element} The marker.
 */
export function RowAsideMarker({
  announced,
  text,
  label,
  className,
}: {
  announced: string;
  text: string;
  label: string;
  className: string;
}) {
  const named = text.toLowerCase().startsWith(label.toLowerCase())
    ? `${announced}: ${text}`
    : `${announced}, ${label}: ${text}`;
  return (
    <Toggletip align="top">
      <ToggletipButton label={named}>
        <span className={className}>{label}</span>
      </ToggletipButton>
      <ToggletipContent>{text}</ToggletipContent>
    </Toggletip>
  );
}

export function EditAffordance({
  node,
  editable,
  locked,
  control,
  onEdit,
  rowLabel,
}: {
  node: SlotInputNode;
  editable: boolean;
  locked: boolean;
  onEdit: ((node: SlotInputNode, rowLabel: string) => void) | undefined;
  /** Which editor this row opens. `null` means the registry permits an edit and no control
   *  exists for it — render nothing rather than a button that opens the wrong editor. */
  control: EditControl | null;
  /** The name of the ROW this control sits in, when that is not the node's own label — the
   *  three scheduled dates are edited from their Milestones row. Announcing the
   *  node's label there would name a different row than the one the user is on. Defaults to
   *  the node's label, which is what every other call site wants. */
  rowLabel?: string;
}) {
  const announced = rowLabel ?? node.label;
  if (!editable) {
    return null;
  }
  if (locked) {
    return (
      <Toggletip align="top">
        {/* Same reasoning as the Edit button below: in a column of these the word "locked"
            alone does not say WHAT is locked. The toggletip's own content keeps the bare
            message, so anything reading the explanation is unaffected. */}
        <ToggletipButton label={`${announced}: ${REPORTED_LOCK_MESSAGE}`}>
          <span className={styles.rowLocked}>locked</span>
        </ToggletipButton>
        <ToggletipContent>{REPORTED_LOCK_MESSAGE}</ToggletipContent>
      </Toggletip>
    );
  }
  // No handler means the tearsheet was opened from a surface that owns no override log
  // (package mode). The row is still marked editable by the registry, so showing a button
  // that cannot write would be worse than showing none.
  if (!onEdit) {
    return null;
  }
  // Overridable, but nothing here knows how to edit it. Same reasoning as the line above, and
  // the reason controlForNode returns null rather than defaulting to the date editor.
  if (control === null) {
    return null;
  }
  return (
    <Button
      kind="ghost"
      size="sm"
      className={styles.rowEditButton}
      // ── THE LABEL SAYS WHICH ROW, THE TEXT STAYS "Edit" ──────────────────────────────
      //
      // Eleven of these now line up in one column, and read aloud they were eleven buttons
      // called "Edit" distinguished only by their position in the table — which is no
      // distinction at all to anyone not looking at it. The visible text is unchanged (the
      // column is legible precisely because the word is short and repeated); only the
      // ACCESSIBLE name gains the row, which is the one a screen reader announces.
      aria-label={`Edit ${announced}`}
      // ── THE ROW THE USER CLICKED, CARRIED TO THE EDITOR ───────────────────────────────
      //
      // `announced` is this row's name — the node's label everywhere except the three
      // scheduled dates, which are edited from their Milestones row. The modal headed itself
      // from the NODE, so clicking "Completion Date (in use)" opened "Edit Completion Date
      // (scheduled)" — a different row on the same page, and half of the placement fix
      // undone. Passed from here rather than re-derived there, so one row has one name.
      onClick={() => onEdit(node, announced)}
    >
      Edit
    </Button>
  );
}

/**
 * A Parameter/Value (or Input/Value, Output/Value) table — the shared table shape
 * every tearsheet section uses.
 *
 * @param {object} props - The left-column header and the rows.
 * @return {JSX.Element} The table.
 */
export function ValueTable({
  head,
  children,
}: {
  head: string;
  children: ReactNode;
}) {
  return (
    <table className={styles.paramTable}>
      <thead>
        <tr>
          <th className={styles.paramNameHead}>{head}</th>
          <th className={styles.paramValueHead}>Value</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
